import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Image } from '../types';
import { useAuth } from './AuthContext';
import { favoritesService } from '../services/favoritesService';
import { analytics } from '../services/analytics';

interface FavoritesContextType {
  favorites: Image[];
  addToFavorites: (image: Image) => Promise<void>;
  removeFromFavorites: (imageId: string) => Promise<void>;
  isFavorite: (imageId: string) => boolean;
  loading: boolean;
  preloadFavorites: (userId: number, googleId: string) => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favorites, setFavorites] = useState<Image[]>([]);
  const [loading, setLoading] = useState(false);
  const { user, registerPreloadFunction } = useAuth();

  // Cache inteligente + precarga
  const loadFavorites = async (userId: number, googleId: string, forceRefresh = false) => {
    // PASO 1: Cargar INMEDIATAMENTE desde localStorage (rápido)
    if (!forceRefresh) {
      const storedFavorites = localStorage.getItem(`favorites_${googleId}`);
      if (storedFavorites) {
        setFavorites(JSON.parse(storedFavorites));
      }
    }

    // PASO 2: Actualizar desde backend en background
    try {
      const backendFavorites = await favoritesService.getFavorites(userId);
      setFavorites(backendFavorites);
      // Actualizar cache para próxima vez
      localStorage.setItem(`favorites_${googleId}`, JSON.stringify(backendFavorites));
    } catch (error) {
      console.error('Error loading favorites from backend:', error);
      // Si falla y no hay cache, mantener array vacío
      if (!localStorage.getItem(`favorites_${googleId}`)) {
        setFavorites([]);
      }
    }
  };

  useEffect(() => {
    const initializeFavorites = async () => {
      if (user?.userId) {
        setLoading(true);
        await loadFavorites(user.userId, user.id);
        setLoading(false);
      } else if (user) {
        // Solo localStorage si no hay backend ID
        const storedFavorites = localStorage.getItem(`favorites_${user.id}`);
        if (storedFavorites) {
          setFavorites(JSON.parse(storedFavorites));
        }
      } else {
        setFavorites([]);
      }
    };

    initializeFavorites();
  }, [user]);

  const saveFavorites = (updatedFavorites: Image[]) => {
    if (user) {
      localStorage.setItem(`favorites_${user.id}`, JSON.stringify(updatedFavorites));
    }
  };

  const addToFavorites = async (image: Image) => {
    if (!isFavorite(image.id) && user) {
      try {
        if (user.userId) {
          await favoritesService.addFavorite({
            idCliente: user.userId,
            idImagen: parseInt(image.id),
            secuencia: favorites.length + 1
          });
        }
        
        const updatedFavorites = [...favorites, image];
        setFavorites(updatedFavorites);
        saveFavorites(updatedFavorites);
        
        // Track add to favorites
        analytics.addToFavorites(image.id);
      } catch (error) {
        console.error('Error adding favorite:', error);
      }
    }
  };

  const removeFromFavorites = async (imageId: string) => {
    if (user) {
      try {
        if (user.userId) {
          await favoritesService.removeFavorite(user.userId, parseInt(imageId));
        }
        
        const updatedFavorites = favorites.filter(img => img.id !== imageId);
        setFavorites(updatedFavorites);
        saveFavorites(updatedFavorites);
        
        // Track remove from favorites
        analytics.removeFromFavorites(imageId);
      } catch (error) {
        console.error('Error removing favorite:', error);
      }
    }
  };

  const isFavorite = (imageId: string) => {
    return favorites.some(img => img.id === imageId);
  };

  // Exponer función de precarga para AuthContext
  const preloadFavorites = async (userId: number, googleId: string) => {
    await loadFavorites(userId, googleId);
  };

  // Registrar función de precarga en AuthContext
  React.useEffect(() => {
    if (registerPreloadFunction) {
      registerPreloadFunction('favorites', preloadFavorites);
    }
  }, [registerPreloadFunction]);

  return (
    <FavoritesContext.Provider value={{ favorites, addToFavorites, removeFromFavorites, isFavorite, loading, preloadFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
};